import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    preferredSpeedUnitSelector,
    primarySportSelector,
    updateUserData,
    userSelector,
} from 'redux/reducers/user';
import { useAppDispatch } from 'redux/store';

export type PreferredSpeedUnit = 'mph' | 'fps';

const MPH_TO_FPS = 1.46667;
const MPS_TO_MPH = 2.23694;
const MPS_TO_FPS = 3.28084;

function isBaseballSport(sport: string | null): boolean {
    return sport === 'baseball' || sport === 'softball';
}

/**
 * Converts a speed value from MPH to the user's preferred unit.
 */
export function convertMphToPreferred(mph: number, unit: PreferredSpeedUnit): number {
    return unit === 'fps' ? mph * MPH_TO_FPS : mph;
}

/**
 * Converts a speed value from m/s to the user's preferred unit.
 */
export function convertMpsToPreferred(mps: number, unit: PreferredSpeedUnit): number {
    return unit === 'fps' ? mps * MPS_TO_FPS : mps * MPS_TO_MPH;
}

/**
 * Converts a speed value from the user's preferred unit back to MPH.
 */
export function convertPreferredToMph(value: number, unit: PreferredSpeedUnit): number {
    return unit === 'fps' ? value / MPH_TO_FPS : value;
}

/**
 * Returns the display label for the current speed unit.
 */
export function speedUnitLabel(unit: PreferredSpeedUnit): string {
    return unit === 'fps' ? 'ft/s' : 'mph';
}

export function useSpeedUnitPreference() {
    const dispatch = useAppDispatch();
    const primarySport = useSelector(primarySportSelector);
    const storedUnit = useSelector(preferredSpeedUnitSelector);
    const { userData } = useSelector(userSelector);

    const unit: PreferredSpeedUnit = useMemo(() => {
        if (storedUnit === 'mph' || storedUnit === 'fps') return storedUnit;
        return isBaseballSport(primarySport) ? 'fps' : 'mph';
    }, [storedUnit, primarySport]);

    const setUnit = useCallback(
        (u: PreferredSpeedUnit) => {
            if (!userData?.uuid) return;
            dispatch(
                updateUserData({
                    userUuid: userData.uuid,
                    first_name: null,
                    last_name: null,
                    email: null,
                    user_name: null,
                    phone: null,
                    profile_photo: null,
                    preferred_speed_unit: u,
                }),
            );
        },
        [dispatch, userData?.uuid],
    );

    const isBaseball = isBaseballSport(primarySport);

    const convert = useCallback((mph: number) => convertMphToPreferred(mph, unit), [unit]);

    const convertBack = useCallback((value: number) => convertPreferredToMph(value, unit), [unit]);

    const label = speedUnitLabel(unit);

    return { unit, setUnit, isBaseball, convert, convertBack, label };
}
